// UCC tab — header strip. Three summary tiles (active / continuation
// window open / lapsed) plus an action slot for the "File UCC-1" button.

import type { ReactNode } from "react";
import { continuationWindowOpenNow, isActive, isLapsed } from "./lapse";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";

interface UccHeaderStripProps {
  filings: UccFilingDto[];
  /** Header-row action (the permission-gated "File UCC-1" button). */
  action?: ReactNode;
}

function StatTile({
  label,
  value,
  tone = "neutral",
  testId,
}: {
  label: string;
  value: string;
  tone?: "neutral" | "warn" | "bad";
  testId?: string;
}) {
  const toneClass =
    tone === "warn"
      ? "text-status-warning"
      : tone === "bad"
        ? "text-status-error"
        : "text-onyx";
  return (
    <div
      className="rounded-md border border-border-light p-space-3 bg-white-pure"
      data-testid={testId}
    >
      <div className="text-caption text-gray-mid uppercase tracking-label">{label}</div>
      <div className={`font-display font-bold text-h3 mt-space-1 ${toneClass}`}>{value}</div>
    </div>
  );
}

export function UccHeaderStrip({ filings, action }: UccHeaderStripProps) {
  const activeCount = filings.filter(isActive).length;
  const windowOpenCount = filings.filter(
    (f) => isActive(f) && continuationWindowOpenNow(f),
  ).length;
  const lapsedCount = filings.filter(isLapsed).length;

  return (
    <div
      className="flex items-start justify-between gap-space-3 flex-wrap"
      data-testid="ucc-header-strip"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-2 flex-1 min-w-[16rem]">
        <StatTile
          label="Active filings"
          value={String(activeCount)}
          testId="ucc-stat-active"
        />
        <StatTile
          label="Continuation window open"
          value={String(windowOpenCount)}
          tone={windowOpenCount > 0 ? "warn" : "neutral"}
          testId="ucc-stat-window-open"
        />
        <StatTile
          label="Lapsed"
          value={String(lapsedCount)}
          tone={lapsedCount > 0 ? "bad" : "neutral"}
          testId="ucc-stat-lapsed"
        />
      </div>
      {action && <div className="shrink-0 pt-space-1">{action}</div>}
    </div>
  );
}
